import { CommandProps, Editor, Extension } from "@knowt/editor/core";
import { Plugin, PluginKey } from "@knowt/editor/pm/state";
import { Decoration, DecorationSet } from "@knowt/editor/pm/view";
import { createGhostEl, debounce, isValidSuggestionKey } from "./utils";
import { parsedGhostTextHtml, getSuggestionTrimDoubleSpaces } from "./parser";
import { NodeDetails } from "./type";
import LRU from "./lru";

declare module "@knowt/editor/core" {
    interface Commands<ReturnType> {
        ghostText: {
            /** display the ghost text in the editor's current cursor */
            populateSuggestion: (props: { alternate: boolean }) => ReturnType;
            /** populate suggestion (debounced) */
            tryPopulateSuggestion: (props: { alternate: boolean }) => ReturnType;
            /** we saved the latest suggestion, if refetch is unnecessary then use this */
            populatePreviousSuggestion: () => ReturnType;
            clearSuggestion: () => ReturnType;
            acceptSuggestion: () => ReturnType;
        };
    }

    interface EditorEvents {
        ghostText: { editor: Editor; state: boolean };
    }
}

const ghostTextPluginKey = new PluginKey("ghostText");

export type GhostTextOptions = {
    /** fetch inline suggestions and returns the suggestion string to be shown */
    fetchSuggestion: (props: {
        /** the whole text of current node */ nodeText: string;
        /** the current sentence */ sentence: string;
        /** the context prior to the current sentence */ context: string;
        editor: Editor;
    }) => Promise<string>;

    /** incase the current suggestion is not good, trigger this to refetch the suggestion */
    fetchAlternateSuggestion: (props: {
        nodeText: string;
        sentence: string;
        context: string;
        editor: Editor;
    }) => Promise<string>;

    onSuggestionAccept: (props: {
        /** either suggestion in accepted or dismiss */ state: boolean;
        /** is this a regeneration or initial suggestion */ alternate?: boolean;
        editor: Editor;
    }) => void;

    disabled: boolean | ((props: { editor: Editor }) => boolean);

    /** delay in ms when user stop typing before `fetchSuggestion` called */
    timeout: number;
};

export const GhostText = Extension.create<
    GhostTextOptions,
    {
        ghostTextStorage: {
            currentSuggestion?: string;
            nodeDetails?: NodeDetails;
            isFetching?: boolean;
            alternate?: boolean;
        };
        /** suggestion cache storage */
        lruCache: LRU<string, string>;
    }
>({
    name: "ghostText",

    addOptions() {
        return {
            disabled: false,
            timeout: 1000,
            fetchAlternateSuggestion: async () => {
                throw new Error("`fetchAlternateSuggestion` is not implemented");
            },
            fetchSuggestion: async () => {
                throw new Error("`fetchSuggestion` is not implemented");
            },
            onSuggestionAccept: () => null,
        };
    },

    addStorage() {
        return { ghostTextStorage: {}, lruCache: new LRU(6) };
    },

    addCommands() {
        /** trigger applying/disapplying suggestion */
        const emitSuggestion = ({
            suggestion,
            nodeDetails,
            state,
            alternate,
        }: {
            suggestion: string;
            nodeDetails?: NodeDetails;
            state: boolean;
            alternate?: boolean;
        }) => {
            const editor = this.editor;
            editor.emit("ghostText", { editor, state });
            this.storage.ghostTextStorage = {
                currentSuggestion: suggestion.trimEnd(),
                nodeDetails,
                isFetching: false,
                alternate,
            };
            editor.view.dispatch(editor.view.state.tr.setMeta("addToHistory", false));
        };

        const handlePopulateSuggestion = ({ state, editor, alternate }: { alternate: boolean } & CommandProps) => {
            const isDisabled = () => {
                const disabled = this.options.disabled;
                if (typeof disabled === "function") {
                    return disabled({ editor });
                }
                return disabled;
            };

            if (isDisabled()) {
                this.storage.lruCache.clear();
                return false;
            }

            if (!this.editor.isFocused) return false;

            const { $from } = state.selection;
            const node = $from.parent;
            const nodeText = node.textContent;

            const [from, to] = [$from.start() - 1, $from.end() + 1];
            const pos = $from.pos;

            // only generate if it's the end of node. `5` is just approximate length of incase user add too many space at the end
            if (pos < to - 5) return false;

            const currentSentence = nodeText.split(".").at(-1) ?? "";

            // get editor html before the current node
            const previousContent = editor.view.state.doc.textBetween(
                0,
                Math.max(from, to) - currentSentence.length,
                "\n"
            );

            // trim to a proper few sentences behind this.
            // select 3 recent paragraphs or sentences, whichever is shorter
            const recentParagraphs = previousContent
                .split("\n")
                .filter(p => p.length > 0)
                .slice(-4)
                .join("\n");

            const context = recentParagraphs.split(".").slice(-6).join(".");

            const nodeDetails = { from, to, pos };

            // get the value from cache if it exists
            const lruCache = this.storage.lruCache;
            const lruKey = JSON.stringify({ nodeDetails, currentSentence });
            const cached = lruCache.get(lruKey);
            if (cached && !alternate) {
                emitSuggestion({ suggestion: cached, nodeDetails, state: true });
                return true;
            }

            // set loading state
            this.storage.ghostTextStorage = { nodeDetails, isFetching: true };
            editor.view.dispatch(editor.view.state.tr);

            const fetcher = alternate ? this.options.fetchAlternateSuggestion : this.options.fetchSuggestion;
            fetcher({ nodeText, sentence: currentSentence, context, editor }).then(res => {
                if (!editor.isFocused) {
                    return;
                }
                // save the value to cache first
                lruCache.update(lruKey, res);
                emitSuggestion({ suggestion: res, nodeDetails, state: true, alternate });
            });

            return true;
        };

        const debouncePopulateSuggestion = debounce(handlePopulateSuggestion, this.options.timeout);

        return {
            acceptSuggestion:
                () =>
                ({ chain, editor, state }) => {
                    const storage = this.storage.ghostTextStorage;
                    const currSuggestion = getSuggestionTrimDoubleSpaces({
                        state,
                        suggestion: storage.currentSuggestion || "",
                    });

                    const html = parsedGhostTextHtml({ editor, suggestion: currSuggestion, state });

                    if (currSuggestion) {
                        this.options.onSuggestionAccept({
                            state: true,
                            alternate: storage.alternate,
                            editor: this.editor,
                        });
                        chain().clearSuggestion().insertContent(html).insertContent(" ").focus().run();
                        return true;
                    }

                    return false;
                },

            clearSuggestion: () => () => {
                emitSuggestion({ suggestion: "", nodeDetails: undefined, state: false, alternate: undefined });
                return true;
            },

            populatePreviousSuggestion: () => () => {
                const storage = this.storage.ghostTextStorage;

                const currentSuggestion = storage.currentSuggestion;
                const nodeDetails = storage.nodeDetails;
                const alternate = storage.alternate;

                if (!currentSuggestion || !nodeDetails) return false;

                nodeDetails.pos = nodeDetails.pos + 1;

                // we need to emit it on next tick otherwise it will get cleared immediately
                setTimeout(
                    () =>
                        emitSuggestion({ suggestion: currentSuggestion.slice(1), nodeDetails, state: true, alternate }),
                    1
                );

                return true;
            },

            populateSuggestion:
                ({ alternate }: { alternate: boolean }) =>
                (commandProps: CommandProps) =>
                    handlePopulateSuggestion({ alternate, ...commandProps }),

            tryPopulateSuggestion:
                ({ alternate }: { alternate: boolean }) =>
                (commandProps: CommandProps) => {
                    debouncePopulateSuggestion({ alternate, ...commandProps });
                    return false;
                },
        };
    },

    addProseMirrorPlugins() {
        const getEditor = () => this.editor;
        const getState = () => this.editor.state;
        const getStorage = () => this.storage;

        const populateSuggestion_ = p => this.editor.commands.populateSuggestion(p);
        const tryPopulateSuggestion_ = p => this.editor.commands.tryPopulateSuggestion(p);
        const populatePreviousSuggestion_ = () => this.editor.commands.populatePreviousSuggestion();
        const acceptSuggestion_ = () => this.editor.commands.acceptSuggestion();
        const clearSuggestion_ = () => this.editor.commands.clearSuggestion();

        // we need timeout to make sure `handleKeyDown` returns not interrupting these calls
        const populateSuggestion = (p: { alternate: boolean }) => setTimeout(() => populateSuggestion_(p), 0);
        const tryPopulateSuggestion = (p: { alternate: boolean }) => setTimeout(() => tryPopulateSuggestion_(p), 0);

        const populatePreviousSuggestion = () => setTimeout(() => populatePreviousSuggestion_(), 0);

        const acceptSuggestion = () => setTimeout(acceptSuggestion_, 0);
        const clearSuggestion = () => setTimeout(clearSuggestion_, 0);

        return [
            new Plugin({
                key: ghostTextPluginKey,
                state: {
                    init() {
                        return DecorationSet.empty;
                    },
                    apply(tr) {
                        const storage = getStorage().ghostTextStorage;

                        if (storage.nodeDetails) {
                            const { /* from, to, */ pos } = storage.nodeDetails;

                            // when cursor moved, clear suggestion
                            const cursorPos = tr.selection.$head.pos;
                            if (cursorPos !== pos) {
                                clearSuggestion();
                                return DecorationSet.empty;
                            }

                            const decoration = Decoration.widget(
                                pos,
                                () => {
                                    const { span, container, img } = createGhostEl();

                                    if (storage.isFetching && storage.alternate) {
                                        container.appendChild(img);
                                        container.append("Kai is thinking...");
                                    } else if (storage.currentSuggestion) {
                                        // set suggestion text
                                        const suggestionHTML = parsedGhostTextHtml({
                                            editor: getEditor(),
                                            state: getState(),
                                            suggestion: storage.currentSuggestion,
                                        });

                                        span.innerHTML = suggestionHTML;
                                        container.appendChild(span);
                                    }

                                    return container;
                                },
                                { side: 1 }
                            );

                            return DecorationSet.create(tr.doc, [decoration]);
                        }

                        return DecorationSet.empty;
                    },
                },

                props: {
                    decorations(state) {
                        return this.getState(state);
                    },

                    handleKeyDown: (_, event) => {
                        const submitRejectedSuggestion = () => {
                            const storage = this.storage.ghostTextStorage;
                            if (storage.currentSuggestion) {
                                this.options.onSuggestionAccept({
                                    state: false,
                                    alternate: storage.alternate,
                                    editor: getEditor(),
                                });
                            }
                        };

                        if (!isValidSuggestionKey(event.key)) return false;

                        const currSuggestion = getStorage().ghostTextStorage.currentSuggestion?.trimEnd();
                        const isSuggestionActive = !!currSuggestion;

                        // if user press `Shift + ArrowDown` means they want to cycle through suggestions, even if disabled
                        if (event.key === "ArrowDown" && event.shiftKey) {
                            populateSuggestion({ alternate: isSuggestionActive });
                            return true;
                        }

                        if (isSuggestionActive && event.key === "Escape") {
                            submitRejectedSuggestion();
                            clearSuggestion();
                            return false;
                        }

                        const isAcceptKey = event.key === "ArrowRight" || event.key === "Tab";

                        if (isSuggestionActive && isAcceptKey) {
                            event.preventDefault();
                            acceptSuggestion();
                            return true;
                        }

                        // if user keep typing while suggestion is active
                        if (isSuggestionActive && !isAcceptKey) {
                            const nextChar = currSuggestion[0].toLowerCase();
                            const keyPressed = event.key.toLowerCase();

                            // if user type as the suggestion first character
                            if (keyPressed === nextChar) {
                                populatePreviousSuggestion();
                                return false;
                            }

                            submitRejectedSuggestion();
                            clearSuggestion();
                            tryPopulateSuggestion({ alternate: false });
                            return false;
                        }

                        // otherwise, ignore if user press `ArrowDown` or `ArrowRight`
                        if (event.key === "ArrowDown" || event.key === "ArrowRight") {
                            return false;
                        }

                        if (!isSuggestionActive) {
                            tryPopulateSuggestion({ alternate: false });
                            return false;
                        }

                        return false;
                    },
                },
            }),
        ];
    },
});
